import StyledButtonV2 from "@/components/app/StyledButtonV2";
import CustomDatePicker from "@/components/CustomDatePicker";
import CustomSelect from "@/components/CustomSelect";
import CustomTextArea from "@/components/CustomTextArea";
import StyledModal from "@/components/globalModal/StyledModal";
import Input from "@/components/Input";
import LabeledSwitch from "@/components/LabeledSwitch";
import MaskMultiSelectScroll from "@/components/MaskMultiSelectScroll";
import { DisclosureHost } from "@/hooks/use-disclosure";
import { useQueryParams } from "@/hooks/use-query-param";
import { useSnackbar } from "@/hooks/useSnackbar";
import { usePatientAllergiesCreate } from "@/queries/allergies/use-patient-allergies-create";
import { usePatientAllergyList } from "@/queries/allergies/use-patient-allergies-list";
import { usePatientAllergiesUpdate } from "@/queries/allergies/use-patient-allergies-update";
import { useExportClinicalToEncounter } from "@/queries/encounter/use-export-clinical-to-encounter";
import {
  ExportClinicalDataToEncounterTypeEnum,
  Patientallergy,
} from "@/services";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
} from "@/utils/utils";
import { Box, Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  ALLERGY_INITIAL_VALUES,
  ALLERGY_VALIDATION,
  SEVERITY_LIST,
  STATUS_LIST,
} from "./allergy-constant";

interface UpsertAllergiesDrawerProps {
  disclosureHost: DisclosureHost;
  isEAllergy?: boolean;
}

const UpsertAllergiesDrawer = ({
  disclosureHost,
  isEAllergy: _ = false,
}: UpsertAllergiesDrawerProps) => {
  // const [profileData]: any = useSelectedPatient();
  const isVisitNotePage = window.location.pathname.includes("/visit-note");
  const [variant, setVariant] = useState<"import" | "default">("default");
  // const [note, setNote] = useState("");
  const [allergies, setAllergies] = useState<any>();

  // const AllergyForm = () => {
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  const { mutateAsync: createAllergy } = usePatientAllergiesCreate();
  const { mutateAsync: updateAllergy } = usePatientAllergiesUpdate();
  const { mutateAsync: exportClinical } = useExportClinicalToEncounter();

  const { patientId } = useParams() as { patientId: string };
  const appointmentid = useQueryParams("appointmentid");

  const [allergyPagination, setAllergyPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);

  const {
    data: allergyList,
    isFetching: isAllergyFetching,
  } = usePatientAllergyList({
    patientUuid: patientId ?? "",
    pagination: allergyPagination,
    gridSorting: [],
    archive: false,
    status: true,
    enabled: variant === "import",
  });

  const allergyOptions = useMemo(() => {
    const fetched =
      allergyList?.data.content.map((allergy) => ({
        value: allergy.uuid,
        label: (allergy as any)?.allergy,
      })) ?? [];
    const selected = (allergies ?? []) as { [k: string]: string }[];
    const idKey = Object.keys(fetched[0] ?? selected[0] ?? {})[0];
    const missing = selected.filter(
      (s) => !fetched.some((o) => (o as any)[idKey] === s[idKey])
    );
    return [...missing, ...fetched];
  }, [allergyList?.data.content, allergies]);

  async function handleCreateAllergies(values?: any) {
    const selectedPatient = { [patientId]: "" };

    const payload = {
      patientAllergy: {
        ...values,
        patient: selectedPatient,
        appointmentId: appointmentid ?? undefined,
      },
      xTENANTID: getXtenantId(),
    };

    if (disclosureHost?.data) showToastAndResetForm(updateAllergy(payload));
    else showToastAndResetForm(createAllergy(payload));
  }

  async function handleImportAllergies() {
    const recordUuids = (allergies ?? [])
      .map((record: any) => record?.value)
      .filter(Boolean);

    if (!recordUuids.length) {
      showSnackbar(getErrorSnackbar("Please select at least one record."));
      return;
    }

    showToastAndResetForm(
      exportClinical({
        type: ExportClinicalDataToEncounterTypeEnum.Allergy,
        appointmentUuid: appointmentid ?? "",
        recordUuids,
        xTENANTID: getXtenantId(),
      })
    );
  }

  async function showToastAndResetForm(apiCall: Promise<any>) {
    apiCall
      .then((res) => {
        showSnackbar(getSuccessSnackbar(res));

        queryClient.invalidateQueries({
          queryKey: ["patient-allergy-list"],
        });

        closeAndReset();
      })
      .catch((err) => {
        showSnackbar(getErrorSnackbar(err));
      });
  }

  function closeAndReset() {
    formik.resetForm();
    setVariant("default");
    setAllergies(undefined);
    disclosureHost.close();
  }

  const formik = useFormik<Omit<Patientallergy, "patient">>({
    initialValues: disclosureHost?.data || ALLERGY_INITIAL_VALUES,
    validationSchema: ALLERGY_VALIDATION,
    onSubmit: handleCreateAllergies,
    enableReinitialize: true,
  });

  if (!disclosureHost?.opened) return null;

  return (
    <StyledModal
      open={disclosureHost?.opened}
      onClose={closeAndReset}
      dialogTitle={disclosureHost?.data ? "Edit Allergy" : "Add Allergy"}
      dialogAction={
        <StyledButtonV2
          label={variant === "import" ? "Import" : "Save"}
          variant="contained"
          onClick={
            variant === "import" ? handleImportAllergies : formik.submitForm
          }
        />
      }
      sx={{
        "& .MuiDialog-paper": {
          width: "40vw",
          // height: "42vh",
        },
      }}
    >
      <Box pt={2}>
        {isVisitNotePage && (
          <Box pb={2} px={1.5}>
            <LabeledSwitch
              label="import from existing records"
              checked={variant === "import"}
              onChange={(e) => {
                setVariant(e.target.checked ? "import" : "default");
              }}
            />
          </Box>
        )}

        {variant === "import" && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <MaskMultiSelectScroll
                required
                loading={isAllergyFetching}
                label="Allergy Name"
                optionWithCheckbox={true}
                placeholder="Select Allergy from Existing Medical Records"
                options={allergyOptions}
                value={allergies}
                onChange={(v) => setAllergies(v)}
                pagination={allergyPagination}
                setPagination={setAllergyPagination}
                totalElements={allergyList?.data?.page?.totalElements}
              />
            </Grid>
            {/* 
            <Grid item xs={12}>
              <CustomTextArea
                label="Note"
                id="outlined-multiline-static"
                rows={3}
                variant="outlined"
                name="note"
                placeholder="Type here"
                onBlur={formik.handleBlur}
                value={note}
                onChange={(e) => {
                  setNote(e.target.value);
                }}
              />
            </Grid> */}
          </Grid>
        )}

        {variant === "default" && (
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <Input
                label="Allergy Name"
                required
                textFieldProps={{
                  placeholder: "Enter Allergy Name",
                  name: "allergy",
                  value: formik.values.allergy,
                  onChange: formik.handleChange,
                  onBlur: formik.handleBlur,
                }}
                error={formik.touched.allergy && formik.errors.allergy}
              />
            </Grid>
            <Grid item xs={6}>
              <CustomSelect
                label="Severity"
                placeholder="Select Severity"
                options={SEVERITY_LIST}
                value={formik.values?.severity}
                onChange={(v) => formik.setFieldValue("severity", v)}
              />
            </Grid>
            <Grid item xs={6}>
              <Input
                label="Reaction"
                textFieldProps={{
                  placeholder: "Enter Reaction",
                  name: "reaction",
                  value: formik.values.reaction,
                  onChange: formik.handleChange,
                  onBlur: formik.handleBlur,
                }}
                // error={touched.cpt_code && errors.cpt_code}
              />
            </Grid>
            <Grid item xs={6}>
              <CustomDatePicker
                placeholder="Choose Date"
                maxDate={dayjs(new Date())}
                value={
                  formik.values?.onSetDate
                    ? new Date(formik.values.onSetDate)
                    : undefined
                }
                label="Onset Date"
                onChange={(val) =>
                  formik.setFieldValue("onSetDate", val?.toISOString())
                }
              />
            </Grid>
            <Grid item xs={6}>
              <CustomSelect
                value={formik.values.active as any}
                required
                label="Status"
                placeholder="Select"
                name="active"
                handleBlur={formik.handleBlur}
                disableSortOptions
                options={STATUS_LIST as any}
                onChange={(v) =>
                  formik.setFieldValue("active", (v ?? undefined) as any)
                }
                error={formik.touched.active && formik.errors.active}
              />
            </Grid>
            <Grid item xs={12}>
              <CustomTextArea
                label="Note"
                id="outlined-multiline-static"
                rows={3}
                variant="outlined"
                name="note"
                placeholder="Type here"
                onBlur={formik.handleBlur}
                value={formik.values?.note}
                onChange={formik.handleChange}
              />
            </Grid>
          </Grid>
        )}
      </Box>
    </StyledModal>
  );
};

// const ScriptsureAllergyForm = () => {
//   const { patientId } = useParams() as { patientId: string };

//   const user = sessionStorage.getItem("user");
//   const providerId = user ? JSON.parse(user ?? "")?.uuid : null;
//   const {
//     data: patientScriptsureUrl,
//     isLoading,
//     error,
//     refetch,
//   }: any = useGetPatientScriptsureUrl({
//     patientUuid: patientId ?? "",
//     providerId: providerId ?? "",
//     xTENANTID: getXtenantId() ?? "",
//     type: GetScriptsureProfileUrlTypeEnum.Allergy,
//     enabled: disclosureHost.opened,
//   });
//   return (
//     <StyledFormModal
//       open={disclosureHost?.opened}
//       onClose={() => disclosureHost.close()}
//       dialogTitle={disclosureHost?.data ? "Edit Allergy" : "Add Allergy"}
//       sx={{
//         "& .MuiDialog-paper": {
//           width: "60vw",
//           height: "70vh",
//         },
//       }}
//     >
//       <Box pt={2}>
//         {isLoading && <Text>Loading...</Text>}
//         {error && <Button onClick={refetch}>Refetch</Button>}
//         {patientScriptsureUrl?.data && !isLoading && !error && (
//           <ScriptsureIframe
//             url={patientScriptsureUrl?.data}
//             width="100%"
//             height="calc(100vh-300px)"
//           />
//         )}
//       </Box>
//     </StyledFormModal>
//   );
// };

// profileData?.patientScriptsureId ? (
// return isEAllergy ? <ScriptsureAllergyForm /> :
// return <AllergyForm />;
// };

export default UpsertAllergiesDrawer;
